import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  AlertCircle,
  MessageCircle,
  Send,
  Building2,
  Trophy,
  MapPin,
  ShoppingBag,
  CalendarRange,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import chatService, { Chat, ChatMessage } from '../services/chatService';

const TEAL = '#3FCED2';

const LIST_POLL_INTERVAL = 30000; // 30s für Konversationsliste
const CHAT_POLL_INTERVAL = 10000; // 10s für offenen Chat

function entryTypeIcon(entryType?: string | null): React.ReactNode {
  switch (entryType) {
    case 'TOURNAMENT': return <Trophy size={14} />;
    case 'CLUB': return <Building2 size={14} />;
    case 'PUB': return <MapPin size={14} />;
    case 'SHOP': return <ShoppingBag size={14} />;
    case 'SERIES': return <CalendarRange size={14} />;
    default: return <MessageCircle size={14} />;
  }
}

const ChatsPage: React.FC = () => {
  const { chatId: chatIdParam } = useParams<{ chatId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id || null;

  const activeChatId = chatIdParam || searchParams.get('chat') || null;

  const [chats, setChats] = useState<Chat[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const prevMessageCountRef = useRef<number>(0);
  const activeChatIdRef = useRef<string | null>(null);
  activeChatIdRef.current = activeChatId;

  // ================================================================
  // Daten laden
  // ================================================================

  const loadChats = useCallback(async (silent: boolean = false) => {
    if (!silent) setListLoading(true);
    try {
      const list = await chatService.getMyChats();
      setChats(list);
      setListError(null);
    } catch (err: any) {
      if (!silent) {
        setListError(chatService.extractErrorMessage(err, 'Chats konnten nicht geladen werden.'));
      }
    } finally {
      if (!silent) setListLoading(false);
    }
  }, []);

  const markChatAsRead = useCallback(async (chatId: string) => {
    try {
      const updated = await chatService.markAsRead(chatId);
      if (updated && activeChatIdRef.current === chatId) {
        setActiveChat(updated);
      }
      // Unread-Badge in der Liste lokal zurücksetzen
      setChats((prev) =>
        prev.map((c) => {
          if (c.id !== chatId) return c;
          if (c.ownerId === currentUserId) return { ...c, unreadCountOwner: 0 };
          return { ...c, unreadCountUser: 0 };
        })
      );
    } catch (_e) {
      // Als-gelesen-Markieren darf die Anzeige nicht blockieren
    }
  }, [currentUserId]);

  const loadActiveChat = useCallback(async (chatId: string, silent: boolean = false) => {
    if (!silent) {
      setChatLoading(true);
      setChatError(null);
    }
    try {
      const chat = await chatService.getChatById(chatId);
      if (activeChatIdRef.current !== chatId) return;
      if (!chat) {
        setChatError('Chat nicht gefunden.');
        setActiveChat(null);
        return;
      }
      setActiveChat(chat);
      setChatError(null);
      // Ungelesene Nachrichten des Partners als gelesen markieren
      if (chatService.getUnreadCountForUser(chat, currentUserId) > 0) {
        markChatAsRead(chatId);
      }
    } catch (err: any) {
      if (activeChatIdRef.current !== chatId) return;
      if (!silent) {
        const status = err?.response?.status;
        if (status === 403) {
          setChatError('Du hast keine Berechtigung für diesen Chat.');
        } else if (status === 404) {
          setChatError('Chat nicht gefunden.');
        } else {
          setChatError(chatService.extractErrorMessage(err, 'Chat konnte nicht geladen werden.'));
        }
        setActiveChat(null);
      }
    } finally {
      if (!silent) setChatLoading(false);
    }
  }, [currentUserId, markChatAsRead]);

  // Initiales Laden + Polling der Liste (30s)
  useEffect(() => {
    loadChats();
    const interval = setInterval(() => loadChats(true), LIST_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [loadChats]);

  // Aktiven Chat laden + Polling (10s)
  useEffect(() => {
    if (!activeChatId) {
      setActiveChat(null);
      setChatError(null);
      return;
    }
    prevMessageCountRef.current = 0;
    loadActiveChat(activeChatId);
    const interval = setInterval(() => loadActiveChat(activeChatId, true), CHAT_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [activeChatId, loadActiveChat]);

  // Auto-Scroll bei neuen Nachrichten
  const messageCount = activeChat?.messages?.length || 0;
  useEffect(() => {
    if (messageCount > 0 && messageCount !== prevMessageCountRef.current) {
      prevMessageCountRef.current = messageCount;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messageCount, activeChatId]);

  // ================================================================
  // Aktionen
  // ================================================================

  const handleSelectChat = (chat: Chat) => {
    if (!chat.id || chat.id === activeChatId) return;
    setMessageInput('');
    setSendError(null);
    navigate(`/nachrichten/${chat.id}`);
  };

  const handleBackToList = () => {
    navigate('/nachrichten');
  };

  const handleSend = async () => {
    const content = messageInput.trim();
    if (!content || !activeChatId || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const updated = await chatService.sendMessage(activeChatId, content);
      if (updated) {
        setActiveChat(updated);
        setMessageInput('');
        // Liste aktualisieren (letzte Nachricht / Sortierung)
        loadChats(true);
      } else {
        setSendError('Nachricht konnte nicht gesendet werden.');
      }
    } catch (err: any) {
      setSendError(chatService.extractErrorMessage(err, 'Nachricht konnte nicht gesendet werden.'));
    } finally {
      setSending(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ================================================================
  // Abgeleitete Daten
  // ================================================================

  const sortedChats = useMemo(() => {
    return [...chats].sort((a, b) => (b.lastMessageTime || b.createdAt || 0) - (a.lastMessageTime || a.createdAt || 0));
  }, [chats]);

  const messages = useMemo<ChatMessage[]>(() => activeChat?.messages || [], [activeChat]);

  // ================================================================
  // Render
  // ================================================================

  return (
    <>
      <Helmet>
        <title>Nachrichten | DartCloud</title>
        <meta name="description" content="Deine Nachrichten und Konversationen auf DartCloud." />
        <link rel="canonical" href="https://dartcloudapp.de/nachrichten" />
      </Helmet>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-6 sm:py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(63,206,210,0.1)' }}>
            <MessageCircle size={22} style={{ color: TEAL }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white -tracking-[0.5px]">Nachrichten</h1>
        </div>

        <div
          className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden flex"
          style={{ height: 'calc(100vh - 230px)', minHeight: 480 }}
        >
          {/* LINKS: Konversationsliste */}
          <div
            className={`${activeChatId ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] lg:w-[380px] shrink-0 flex-col border-r border-zinc-800`}
          >
            <div className="px-4 py-3 border-b border-zinc-800 text-sm font-semibold text-zinc-400">
              Konversationen
            </div>
            <div className="flex-1 overflow-y-auto">
              {listLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-4 border-zinc-700 border-t-white rounded-full animate-spin" />
                </div>
              ) : listError ? (
                <div className="p-6 text-center">
                  <AlertCircle size={32} className="mx-auto mb-3 text-zinc-500" />
                  <p className="text-sm text-zinc-400">{listError}</p>
                  <button
                    onClick={() => loadChats()}
                    className="mt-4 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm text-white transition"
                  >
                    Erneut versuchen
                  </button>
                </div>
              ) : sortedChats.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle size={40} className="mx-auto mb-3 text-zinc-600" />
                  <p className="text-sm font-semibold text-white mb-1">Keine Nachrichten</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Starte eine Konversation über die Detailseite eines Turniers, Vereins, einer Spielstätte, eines Shops oder einer Turnierserie.
                  </p>
                </div>
              ) : (
                sortedChats.map((chat) => (
                  <ConversationListItem
                    key={chat.id || `${chat.entryId}-${chat.userId}`}
                    chat={chat}
                    currentUserId={currentUserId}
                    isActive={chat.id === activeChatId}
                    onClick={() => handleSelectChat(chat)}
                  />
                ))
              )}
            </div>
          </div>

          {/* RECHTS: Aktiver Chat */}
          <div className={`${activeChatId ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0 bg-zinc-950`}>
            {!activeChatId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <MessageCircle size={48} className="text-zinc-700 mb-4" />
                <p className="text-zinc-400 font-medium">Wähle eine Konversation aus</p>
                <p className="text-sm text-zinc-600 mt-1">Deine Nachrichten erscheinen hier.</p>
              </div>
            ) : chatLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-zinc-700 border-t-white rounded-full animate-spin" />
              </div>
            ) : chatError ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <AlertCircle size={40} className="text-zinc-500 mb-4" />
                <p className="text-zinc-300 font-medium">{chatError}</p>
                <button
                  onClick={handleBackToList}
                  className="mt-4 px-5 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm text-white transition"
                >
                  Zurück zur Übersicht
                </button>
              </div>
            ) : (
              <>
                {/* Chat-Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-900">
                  <button
                    onClick={handleBackToList}
                    className="md:hidden p-2 rounded-lg hover:bg-zinc-800 text-zinc-300 transition"
                    aria-label="Zurück"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-base font-bold"
                    style={{ backgroundColor: 'rgba(63,206,210,0.15)', color: TEAL }}
                  >
                    {(chatService.getPartnerName(activeChat, currentUserId) || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-bold text-white truncate">
                      {chatService.getPartnerName(activeChat, currentUserId)}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-400 truncate">
                      <span style={{ color: TEAL }}>{entryTypeIcon(activeChat?.entryType)}</span>
                      <span className="truncate">
                        {chatService.getEntryTypeLabel(activeChat?.entryType)}
                        {activeChat?.entryName ? ` · ${activeChat.entryName}` : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Nachrichtenverlauf */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
                  {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                      <p className="text-sm text-zinc-500">
                        Noch keine Nachrichten. Schreibe die erste Nachricht!
                      </p>
                    </div>
                  ) : (
                    messages.map((msg, idx) => {
                      const isOwn = !!currentUserId && msg.senderId === currentUserId;
                      const prevMsg = idx > 0 ? messages[idx - 1] : null;
                      const showDaySeparator =
                        chatService.formatDaySeparator(msg.timestamp) !==
                        chatService.formatDaySeparator(prevMsg?.timestamp);
                      return (
                        <React.Fragment key={msg.id || `msg-${idx}`}>
                          {showDaySeparator && (
                            <div className="flex items-center justify-center py-2">
                              <span className="px-3 py-1 rounded-full bg-zinc-800 text-[11px] font-medium text-zinc-400">
                                {chatService.formatDaySeparator(msg.timestamp)}
                              </span>
                            </div>
                          )}
                          <MessageBubble message={msg} isOwn={isOwn} />
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Eingabefeld */}
                <div className="border-t border-zinc-800 bg-zinc-900 p-3">
                  {sendError && (
                    <p className="mb-2 text-xs text-red-400 px-1">{sendError}</p>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                      placeholder="Nachricht schreiben..."
                      rows={1}
                      className="flex-1 resize-none rounded-xl bg-zinc-800 border border-zinc-700 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#3FCED2] transition"
                      style={{ maxHeight: 120 }}
                    />
                    <button
                      onClick={handleSend}
                      disabled={sending || messageInput.trim().length === 0}
                      className="p-3 rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      style={{ backgroundColor: TEAL, color: '#000' }}
                      aria-label="Nachricht senden"
                    >
                      {sending ? (
                        <span className="block w-[18px] h-[18px] border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

// ===================================================================
// Subcomponents
// ===================================================================

const ConversationListItem: React.FC<{
  chat: Chat;
  currentUserId: string | null;
  isActive: boolean;
  onClick: () => void;
}> = ({ chat, currentUserId, isActive, onClick }) => {
  const partnerName = chatService.getPartnerName(chat, currentUserId);
  const preview = chatService.getLastMessagePreview(chat);
  const time = chatService.formatConversationTime(chat.lastMessageTime || chat.createdAt);
  const unread = chatService.getUnreadCountForUser(chat, currentUserId);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3.5 text-left border-b border-zinc-800/60 transition hover:bg-zinc-800/50"
      style={{ backgroundColor: isActive ? 'rgba(63,206,210,0.08)' : undefined }}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-base font-bold"
        style={{ backgroundColor: 'rgba(63,206,210,0.15)', color: TEAL }}
      >
        {(partnerName || '?').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${unread > 0 ? 'font-bold text-white' : 'font-semibold text-zinc-200'}`}>
            {partnerName}
          </span>
          <span className="text-[11px] text-zinc-500 shrink-0">{time}</span>
        </div>
        {chat.entryName && (
          <div className="flex items-center gap-1 mt-0.5 text-[11px] truncate" style={{ color: TEAL }}>
            {entryTypeIcon(chat.entryType)}
            <span className="truncate">{chat.entryName}</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className={`text-xs truncate ${unread > 0 ? 'text-zinc-200 font-medium' : 'text-zinc-500'}`}>
            {chat.lastMessageSenderId && currentUserId && chat.lastMessageSenderId === currentUserId ? 'Du: ' : ''}
            {preview}
          </span>
          {unread > 0 && (
            <span
              className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center"
              style={{ backgroundColor: TEAL, color: '#000' }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const MessageBubble: React.FC<{ message: ChatMessage; isOwn: boolean }> = ({ message, isOwn }) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[80%] sm:max-w-[65%] rounded-2xl px-4 py-2.5 ${isOwn ? 'rounded-br-md' : 'rounded-bl-md bg-zinc-800'}`}
      style={isOwn ? { backgroundColor: TEAL, color: '#000' } : undefined}
    >
      <p className={`text-sm whitespace-pre-wrap break-words ${isOwn ? '' : 'text-zinc-100'}`}>
        {message.content || ''}
      </p>
      <div className={`mt-1 text-[10px] text-right ${isOwn ? 'text-black/60' : 'text-zinc-500'}`}>
        {chatService.formatMessageTime(message.timestamp)}
      </div>
    </div>
  </div>
);

export default ChatsPage;
